"use client";

import { useEffect, useRef } from "react";
import { useForm, type FieldValues, type DefaultValues } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { z } from "zod";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";

export interface InputFieldConfig<T> {
  name: keyof T;
  label: string;
  /** Optional tooltip shown on hover (info icon) */
  tooltip?: string;
  type?: "text" | "number";
  /** "number" = input only, "slider" = slider only, "slider-number" = both, "select" = dropdown */
  inputType?: "number" | "slider" | "slider-number" | "select";
  /** For inputType "select": dropdown options */
  options?: { value: number | string; label: string }[];
  placeholder?: string;
  suffix?: string;
  min?: number;
  max?: number;
  /** Slider range (when inputType is slider/slider-number). Falls back to min/max if not set. */
  sliderMin?: number;
  sliderMax?: number;
  step?: number;
  /** For mobile: "decimal" shows numeric keypad with decimal */
  inputMode?: "decimal" | "numeric" | "text";
  /** Hide number input spinner arrows */
  hideSpinner?: boolean;
}

interface InputFormProps<T extends z.ZodType<FieldValues>> {
  schema: T;
  defaultValues: z.infer<T>;
  fields: InputFieldConfig<z.infer<T>>[];
  onChange: (values: z.infer<T>) => void;
  /** When source field changes, sync target field to the same value */
  syncFields?: { source: keyof z.infer<T>; target: keyof z.infer<T> };
  className?: string;
}

export function InputForm<T extends z.ZodType<FieldValues>>({
  schema,
  defaultValues,
  fields,
  onChange,
  syncFields,
  className,
}: InputFormProps<T>) {
  type FormValues = z.infer<T> & FieldValues;
  const form = useForm<FormValues>({
    // @ts-expect-error - zodResolver generic inference with RHF
    resolver: zodResolver(schema),
    defaultValues: defaultValues as DefaultValues<FormValues>,
    mode: "onChange",
  });

  const prevSourceRef = useRef(
    syncFields
      ? (defaultValues as Record<string, unknown>)[String(syncFields.source)]
      : undefined
  );

  useEffect(() => {
    const subscription = form.watch((formValues) => {
      let valuesToValidate = formValues as Record<string, unknown>;
      if (syncFields) {
        const sourceVal = valuesToValidate[String(syncFields.source)];
        if (sourceVal !== prevSourceRef.current) {
          prevSourceRef.current = sourceVal;
          form.setValue(
            String(syncFields.target) as never,
            sourceVal as never,
            { shouldValidate: true }
          );
          valuesToValidate = { ...formValues, [String(syncFields.target)]: sourceVal };
        }
      }
      const result = schema.safeParse(valuesToValidate);
      if (result.success) {
        onChange(result.data);
      }
    });
    return () => subscription.unsubscribe();
  }, [form, schema, onChange, syncFields]);

  const inputType = (field: InputFieldConfig<z.infer<T>>) =>
    field.inputType ?? "number";

  return (
    <form
      className={cn("space-y-4", className)}
      onSubmit={(e) => e.preventDefault()}
    >
      {fields.map((field) => {
        const isSelect = inputType(field) === "select";
        const showSlider =
          !isSelect &&
          (inputType(field) === "slider" || inputType(field) === "slider-number");
        const showInput =
          !isSelect &&
          (inputType(field) !== "slider" ||
            inputType(field) === "slider-number");
        const fieldValue = form.watch(String(field.name) as never) as unknown;
        const numValue =
          typeof fieldValue === "number" ? fieldValue : Number(fieldValue) || 0;

        const sliderMin = field.sliderMin ?? field.min;
        const sliderMax = field.sliderMax ?? field.max;
        const sliderValue = Math.min(
          Math.max(numValue, sliderMin ?? 0),
          sliderMax ?? 100
        );

        const selectClassName = cn(
          "h-8 w-full min-w-0 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:bg-input/50 disabled:opacity-50 md:text-sm",
          "aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20"
        );

        return (
          <div key={String(field.name)} className="space-y-2">
            <div className="flex items-center gap-1.5">
              <Label
                htmlFor={showInput || isSelect ? String(field.name) : undefined}
                id={showSlider && !showInput ? `${String(field.name)}-label` : undefined}
              >
                {field.label}
              </Label>
              {field.tooltip && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <button
                        type="button"
                        className="inline-flex text-muted-foreground hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded"
                        aria-label={`Info: ${field.tooltip}`}
                      >
                        <Info className="size-3.5" />
                      </button>
                    }
                  />
                  <TooltipContent side="top" className="max-w-[240px]">
                    {field.tooltip}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>

            {isSelect && field.options && (
              <select
                id={String(field.name)}
                className={selectClassName}
                {...form.register(
                  String(field.name) as Parameters<typeof form.register>[0]
                )}
              >
                {field.options.map((opt) => (
                  <option key={String(opt.value)} value={String(opt.value)}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )}

            {showSlider && sliderMin != null && sliderMax != null && (
              <div className="space-y-2">
                <Slider
                  value={[sliderValue]}
                  min={sliderMin}
                  max={sliderMax}
                  step={field.step ?? 1}
                  {...(showSlider && !showInput
                    ? { "aria-labelledby": `${String(field.name)}-label` }
                    : showSlider
                      ? { "aria-label": field.label }
                      : {})}
                  onValueChange={(values) => {
                    const v = Array.isArray(values) ? values[0] : values;
                    if (typeof v === "number") {
                      form.setValue(
                        String(field.name) as never,
                        v as never,
                        { shouldValidate: true }
                      );
                    }
                  }}
                />
                {field.suffix && (
                  <p className="text-xs text-muted-foreground">
                    {numValue}
                    {field.suffix}
                  </p>
                )}
              </div>
            )}

            {showInput && (
              <div className="relative">
                <Input
                  id={String(field.name)}
                  type={field.type ?? "number"}
                  placeholder={field.placeholder}
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  inputMode={field.inputMode}
                  {...form.register(
                    String(field.name) as Parameters<typeof form.register>[0]
                  )}
                  className={cn(
                    "pr-8",
                    field.hideSpinner &&
                      "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  )}
                />
                {field.suffix && (
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                    {field.suffix}
                  </span>
                )}
              </div>
            )}

            {form.formState.errors[String(field.name)] && (
              <p className="text-sm text-destructive">
                {
                  (
                    form.formState.errors as Record<
                      string,
                      { message?: string }
                    >
                  )[String(field.name)]?.message
                }
              </p>
            )}
          </div>
        );
      })}
    </form>
  );
}
